import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { foldersAPI } from '../services/api';

export function useFolderTree() {
  return useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: async () => {
      const res = await foldersAPI.treeAll();
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => foldersAPI.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
    },
  });
}

export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }) => foldersAPI.update(id, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
    },
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => foldersAPI.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
      qc.invalidateQueries({ queryKey: ['files'] });
    },
  });
}

export function usePrefetchFolder(folderId) {
  const qc = useQueryClient();
  return () => {
    if (folderId) {
      qc.prefetchQuery({
        queryKey: ['files', { folderId, page: 1 }],
        queryFn: async () => {
          const res = await foldersAPI.list(folderId);
          return res.data;
        },
        staleTime: 30_000,
      });
    }
  };
}

/**
 * Build breadcrumb path from tree data and a selected folder ID
 */
export function buildFolderPath(tree, targetId) {
  if (!tree || !targetId) return [];

  function search(nodes, path) {
    for (const node of nodes) {
      const current = [...path, { id: node.id, name: node.name }];
      if (node.id === targetId) return current;
      if (node.subfolders?.length) {
        const found = search(node.subfolders, current);
        if (found) return found;
      }
    }
    return null;
  }

  return search(tree, []) || [];
}
